/* global axios */

const accountBase = accountId => `/api/v1/accounts/${accountId}/cw_custom`;

export default {
  // funnelId: a trilha (produto / espaço / peça sob medida). Omitido, o servidor devolve
  // a primeira trilha visível ao usuário — e id velho guardado no navegador nunca vira
  // 404, ele só cai no default.
  // busca: nome parcial OU telefone completo. O telefone precisa estar completo porque a
  // coluna é criptografada em repouso e só aceita igualdade — ver Crm::BoardFilter.
  // agenteId: id do responsável, ou 'sem_dono' para os cards órfãos.
  // Ambos vão ao SERVIDOR de propósito: filtrar na tela só acharia o que já está carregado,
  // e o lead que se procura é justamente o que sumiu no meio de sessenta cards.
  fetchBoard(accountId, funnelId = null, { busca, agenteId } = {}) {
    const params = {};
    if (funnelId) params.funnel_id = funnelId;
    if (busca) params.busca = busca;
    if (agenteId) params.agente_id = agenteId;
    return axios.get(`${accountBase(accountId)}/crm_board`, { params });
  },

  // extras: campos opcionais do mesmo corpo — hoje `objecao` e `objecao_detalhe`, que o
  // passo atrás (Fechamento → caixa de follow-up) exige. Vão soltos no corpo, sem
  // envelope: é assim que o controller lê (`params[:objecao]`), e o spec de request tem
  // de mandar exatamente este formato, ou passa verde enquanto a tela leva 422.
  moveDeal(accountId, dealId, stageId, extras = {}) {
    return axios.patch(
      `${accountBase(accountId)}/crm_board/deals/${dealId}/move_stage`,
      { stage_id: stageId, ...extras }
    );
  },

  // T3: detail panel — full deal payload (lead, compras, tasks, transitions).
  fetchDeal(accountId, dealId) {
    return axios.get(`${accountBase(accountId)}/crm_deals/${dealId}`);
  },

  // T5: deal outcomes.
  // compras is optional — array of {item, variacao, quantidade, data_compra}.
  winDeal(accountId, dealId, compras = []) {
    return axios.patch(`${accountBase(accountId)}/crm_deals/${dealId}/win`, {
      compras,
    });
  },

  // motivo_perda is required; server returns 422 if blank or not in allowed list.
  // motivoPerdaDetalhe is required when motivoPerda === 'outro'; null otherwise.
  loseDeal(accountId, dealId, motivoPerda, motivoPerdaDetalhe = null) {
    return axios.patch(`${accountBase(accountId)}/crm_deals/${dealId}/lose`, {
      motivo_perda: motivoPerda,
      motivo_perda_detalhe: motivoPerdaDetalhe,
    });
  },

  // T7 / F1: task CRUD ("Próxima Ação"). Scoped under a deal.
  // params (createTask/updateTask): { titulo, descricao, vence_em (ISO),
  //   tipo, status, sequencia, assigned_to_id } — backend allowlist (§4).
  fetchTasks(accountId, dealId) {
    return axios.get(`${accountBase(accountId)}/crm_deals/${dealId}/tasks`);
  },

  createTask(accountId, dealId, taskAttrs) {
    return axios.post(
      `${accountBase(accountId)}/crm_deals/${dealId}/tasks`,
      taskAttrs
    );
  },

  updateTask(accountId, dealId, taskId, taskAttrs) {
    return axios.patch(
      `${accountBase(accountId)}/crm_deals/${dealId}/tasks/${taskId}`,
      taskAttrs
    );
  },

  // F1 §4 [BLOQUEANTE]: concluir é atômico. proximaAcao opcional —
  // { tipo, vence_em, descricao?, assigned_to_id?, titulo? }. Quando enviado,
  // o backend cria a próxima ação na MESMA transação (guard §13). Responde 422
  // com { error } se concluir deixaria o deal ativo sem próxima ação.
  // Resposta de sucesso: { task, proxima_acao: <serialized>|null }.
  completeTask(accountId, dealId, taskId, proximaAcao = null) {
    return axios.patch(
      `${accountBase(accountId)}/crm_deals/${dealId}/tasks/${taskId}/complete`,
      proximaAcao ? { proxima_acao: proximaAcao } : {}
    );
  },

  // F4 §7: descartar uma ação pendente (status → cancelada). SEM guard §13
  // (cancelar não é concluir — não precisa exigir a próxima ação). Usado pelo
  // seletor de descarte de ações avulsas e pela interrupção da régua.
  cancelTask(accountId, dealId, taskId) {
    return axios.patch(
      `${accountBase(accountId)}/crm_deals/${dealId}/tasks/${taskId}/cancel`
    );
  },

  // F4 §7: registrar o RESULTADO de um toque de follow-up da régua (síncrono).
  // payload por resultado:
  //   • respondeu | interesse | pediu_prazo | outro → { resultado, proxima_acao }
  //     (o backend interrompe a régua e cria a próxima ação na MESMA transação)
  //   • nao_respondeu → { resultado }  (avança a régua para o próximo toque)
  //   • recusou       → { resultado, motivo_perda, motivo_perda_detalhe? }
  //     (o deal vira PERDIDO — o board precisa refrescar)
  // 422 { error } quando falta a próxima ação exigida ou o motivo é inválido.
  followUpResult(accountId, dealId, taskId, payload) {
    return axios.patch(
      `${accountBase(accountId)}/crm_deals/${dealId}/tasks/${taskId}/follow_up_result`,
      payload
    );
  },

  // T9: CRM metrics aggregation.
  // since / until_at: Unix timestamps (optional).
  // funnelId: a trilha. Sem ele o servidor usa a primeira visível — e a conversão etapa
  // a etapa SÓ faz sentido dentro de uma jornada (as três trilhas têm etapas de mesmo
  // nome; encadeá-las descreveria um funil que não existe).
  fetchMetrics(accountId, { since, untilAt, funnelId } = {}) {
    const params = {};
    if (since) params.since = since;
    if (untilAt) params.until_at = untilAt;
    if (funnelId) params.funnel_id = funnelId;
    return axios.get(`${accountBase(accountId)}/crm_metrics`, { params });
  },

  // T12: CRM feature flag + wait_clock_seconds per account.
  // Always returns 200 — { enabled: bool, wait_clock_seconds: int }.
  fetchCrmSettings(accountId) {
    return axios.get(`${accountBase(accountId)}/crm_settings`);
  },

  // T6: update lead store profile (loja_*). Payload: { loja_segmento, loja_cidade_uf, loja_qtd_lojas }.
  // Returns the full lead object (same shape as deal.lead in fetchDeal response).
  updateLead(accountId, leadId, payload) {
    return axios.patch(`${accountBase(accountId)}/crm_leads/${leadId}`, {
      lead: payload,
    });
  },

  // F4: deal vinculado à conversa aberta no painel de atendimento.
  // Sempre 200 — envelope { deal: <serialized|null>, stages: [...non-terminal asc], agents: [...] }.
  // deal: null quando a conversa não tem deal (inbox interna, conversa pré-CRM) → front esconde o bloco.
  fetchDealByConversation(accountId, conversationId) {
    return axios.get(
      `${accountBase(accountId)}/crm_deals/by_conversation/${conversationId}`
    );
  },

  // F5: fila de perda assistida do operador — deals no fim de régua aguardando decisão
  // humana. Envelope { deals: [...] }; vazio quando a flag crm_sla_automation está OFF na
  // conta (regressão-zero). A confirmação de perda reusa loseDeal() (nenhum endpoint novo).
  fetchPendingLoss(accountId) {
    return axios.get(`${accountBase(accountId)}/crm_deals/pending_loss`);
  },

  // custom (2026-08-07): "Decidir depois" — empurra o marcador 24h. Existe para que ninguém
  // precise inventar um motivo de perda só para sair da tela; motivo inventado entra na
  // métrica que decide onde a operação investe.
  adiarPerda(accountId, dealId) {
    return axios.patch(
      `${accountBase(accountId)}/crm_deals/${dealId}/adiar_perda`
    );
  },

  // F4: atualiza campos editáveis do deal a partir do bloco Kanban na conversa.
  // Allowlist no backend: agente_id, prioridade, vence_em, inicio_em. Nunca lead/stage/resultado.
  // Retorna o deal reserializado (envelope { deal }).
  updateDeal(accountId, dealId, payload) {
    return axios.patch(
      `${accountBase(accountId)}/crm_deals/${dealId}`,
      payload
    );
  },
};
